/**
 * Helper para gerenciar requisitos de contexto das páginas
 * Define quais páginas precisam de qual tipo de contexto
 */

export type ContextRequirement = 'FLEXIBLE' | 'STORE_REQUIRED' | 'NETWORK_REQUIRED';

/**
 * Mapeamento de rotas e seus requisitos de contexto
 */
export const CONTEXT_REQUIREMENTS: Record<string, ContextRequirement> = {
  // ✅ Páginas Flexíveis (funcionam em qualquer modo)
  '/lojas': 'FLEXIBLE',
  '/lojas/nova': 'FLEXIBLE',
  '/lojas/:id': 'FLEXIBLE',
  '/usuarios': 'FLEXIBLE',
  '/usuarios/:id': 'FLEXIBLE',
  '/seguranca': 'FLEXIBLE',
  '/gerenciar': 'FLEXIBLE',
  '/configuracoes': 'FLEXIBLE',
  
  // ✅ Nova Venda usa loja do veículo selecionado automaticamente
  '/vendas/nova': 'FLEXIBLE',  // Loja será inferida do veículo selecionado
  
  // ✅ Páginas Flexíveis para Vendas (Rede Admin vê todas, Store User vê da loja)
  '/vendas': 'FLEXIBLE',
  '/vendas/:id': 'FLEXIBLE',
  '/tarefas': 'FLEXIBLE',
  '/equipe': 'STORE_REQUIRED',
  '/atendimentos': 'STORE_REQUIRED',
  
  // 🔒 Páginas que PRECISAM de modo rede
  '/dashboard/network': 'NETWORK_REQUIRED',
  '/relatorios/consolidado': 'NETWORK_REQUIRED',
};

/**
 * Verifica qual guard deve ser usado para uma rota específica
 */
export const getGuardForRoute = (path: string): ContextRequirement => {
  // Normalizar path (remover query params, etc)
  const normalizedPath = path.split('?')[0];
  
  // Verificar match exato primeiro
  if (CONTEXT_REQUIREMENTS[normalizedPath]) {
    return CONTEXT_REQUIREMENTS[normalizedPath];
  }
  
  // Verificar match com parâmetros dinâmicos
  for (const [pattern, requirement] of Object.entries(CONTEXT_REQUIREMENTS)) {
    if (pattern.includes(':')) {
      const regex = new RegExp('^' + pattern.replace(/:\w+/g, '[^/]+') + '$');
      if (regex.test(normalizedPath)) {
        return requirement;
      }
    }
  }
  
  // Default para páginas flexíveis
  return 'FLEXIBLE';
};

/**
 * Retorna descrição amigável do requisito de contexto
 */
export const getContextRequirementDescription = (requirement: ContextRequirement): string => {
  switch (requirement) {
    case 'FLEXIBLE':
      return 'Funciona em Modo Rede ou Modo Loja';
    case 'STORE_REQUIRED':
      return 'Requer seleção de loja específica';
    case 'NETWORK_REQUIRED':
      return 'Requer Modo Rede (todas as lojas)';
    default:
      return 'Requisito desconhecido';
  }
};

/**
 * Verifica se a rota atual é compatível com o contexto
 */
export const isRouteCompatibleWithContext = (
  path: string,
  hasNetworkId: boolean,
  hasStoreId: boolean
): { compatible: boolean; reason?: string } => {
  const requirement = getGuardForRoute(path);
  
  if (!hasNetworkId) {
    return {
      compatible: false,
      reason: 'Nenhuma rede selecionada'
    };
  }
  
  switch (requirement) {
    case 'FLEXIBLE':
      return { compatible: true };
      
    case 'STORE_REQUIRED':
      if (!hasStoreId) {
        return {
          compatible: false,
          reason: 'Esta página requer seleção de uma loja específica'
        };
      }
      return { compatible: true };
      
    case 'NETWORK_REQUIRED':
      if (hasStoreId) {
        return {
          compatible: false,
          reason: 'Esta página requer Modo Rede (todas as lojas)'
        };
      }
      return { compatible: true };
      
    default:
      return { compatible: true };
  }
};
